import { Component, Input, OnDestroy } from '@angular/core'
import {
  ApiService, AppointmentModel,
  COLOR,
  ConfirmService,
  MessageTemplate,
  Patient,
  PatientPalRestError,
  ProgressIndicatorService,
  SendPatientMessageMethod,
} from '@mmx/shared'
import moment from 'moment-timezone'
import { Subject } from 'rxjs'
import { takeUntil } from 'rxjs/operators'

import {
  SendContactEditDialogResult,
  SendContactEditDialogService,
} from '../../send-contact-edit-dialog'

@Component({
  selector: 'mmx-confirmation-status-icon',
  templateUrl: './confirmation-status-icon.component.html',
  standalone: false,
})
export class ConfirmationStatusIconComponent implements OnDestroy {
  @Input() patient?: Patient
  @Input() appointment: AppointmentModel
  @Input() workflow = false

  public inProgress = false
  private onDestroy$ = new Subject<void>()

  constructor(
    private confirmService: ConfirmService,
    private sendContactEditDialogService: SendContactEditDialogService,
    private progress: ProgressIndicatorService,
    private restService: ApiService,
  ) {}

  ngOnDestroy(): void {
    this.onDestroy$.next()
    this.onDestroy$.complete()
  }

  getAppointmentConfirmationStatusColor(): string {
    if (this.appointment.patientConfirmed === true) {
      return COLOR.GREEN
    } else if (this.appointment.patientConfirmed === false) {
      return COLOR.RED
    } else if (
      this.appointment.patientConfirmed === undefined &&
      this.appointment.lastConfirmMsgSentAt !== undefined
    ) {
      return COLOR.YELLOW
    } else if (
      this.appointment.patientConfirmed === undefined &&
      this.appointment.lastConfirmMsgSentAt === undefined
    ) {
      return COLOR.DEFAULT
    }
  }

  getAppointmentConfirmationStatusTitleText(): string {
    if (
      typeof this.appointment.patientConfirmed === 'boolean' &&
      this.appointment.patientConfirmedAt != null
    ) {
      const confirmationString = this.appointment.patientConfirmed
        ? 'confirmed'
        : 'rejected'
      const at = this.appointment.patientConfirmedAt.clone().local()
      const when = `${at.calendar()} (${at.fromNow()})`
      if (this.appointment.patientConfirmedBy === 'sms') {
        return `Patient ${confirmationString} by text ${when}`
      } else if (this.appointment.patientConfirmedBy === 'link') {
        return `Patient ${confirmationString} by email ${when}`
      } else if (typeof this.appointment.patientConfirmedBy === 'string') {
        return `Manually marked ${confirmationString} ${when}`
      } else {
        return `Patient ${confirmationString} ${when}`
      }
    } else if (
      this.appointment.patientConfirmed === undefined &&
      this.appointment.lastConfirmMsgSentAt !== undefined
    ) {
      return `A confirmation request was last sent to the patient ${this.appointment.lastConfirmMsgSentAt
        .clone()
        .local()
        .calendar()}`
    } else if (
      this.appointment.patientConfirmed === undefined &&
      this.appointment.lastConfirmMsgSentAt === undefined
    ) {
      return `No confirmation requests have been sent yet`
    }
  }

  markAppointmentConfirmation(confirmation: boolean) {
    this.inProgress = true
    const progress = this.progress.start()

    this.restService
      .patchAppointment(this.appointment.id, { confirmation })
      .subscribe(
        (updatedAppointment) => {
          this.inProgress = false
          this.appointment.patientConfirmed =
            updatedAppointment.patientConfirmed
          this.appointment.patientConfirmedAt = moment
            .utc(updatedAppointment.patientConfirmedAt)
            .local()
          this.appointment.patientConfirmedBy =
            updatedAppointment.patientConfirmedBy
          progress.stop()
        },
        () => {
          this.inProgress = false
          progress.stop('Failed to update confirmation status')
        },
      )
  }

  openSendMessageConfirmationDialog(method: SendPatientMessageMethod) {
    if (this.inProgress) {
      return
    }
    this.inProgress = true

    this.sendContactEditDialogService.open({
      method,
      phone: this.patient?.phone,
      email: this.patient?.email,
    })
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((result: SendContactEditDialogResult | undefined) => {
        if (result === undefined) {
          this.inProgress = false
          return
        }

        this.confirmAndSendMessage(method, result.to)
      })
  }

  private confirmAndSendMessage(method: SendPatientMessageMethod, to?: string) {
    const isMessageAlreadySent = this.appointment.lastConfirmMsgSentAt !== undefined
    const confirmMessage = 'This patient has already received a confirmation message. Would you like to send it again?'

    if (!isMessageAlreadySent) {
      this.sendMessage(method, to)
      return
    }

    this.confirmService
      .confirm(confirmMessage)
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((isConfirmed) => {
        if (isConfirmed) {
          this.sendMessage(method, to)
        } else {
          this.inProgress = false
        }
      })
  }

  private sendMessage(method: SendPatientMessageMethod, to?: string) {
    this.inProgress = true
    this.restService
      .sendPatientMessage(this.patient.id, {
        type: MessageTemplate.TYPE.APPOINTMENT_CONFIRMATION,
        method,
        appointmentId: this.appointment.id,
        to,
      })
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(
        () => {
          this.appointment.lastConfirmMsgSentAt = moment()
          this.inProgress = false
          this.progress.snack('Message has been sent')
        },
        (err: PatientPalRestError) => {
          this.inProgress = false
          this.progress.snack({
            message: `Failed to send message. ${err.error?.message}`,
          })
        },
      )
  }
}
